import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Loader2 } from 'lucide-react';
import {
  createZoneWorkingHours,
  deleteZoneWorkingHours,
  listZoneWorkingHours,
  updateZoneWorkingHours,
} from '../../api/workingHours';
import type { ZoneWorkingHoursDto } from '../../api/workingHours';
import type { ScheduleExceptionDraft, ScheduleGroup } from './workingHoursState';
import { getActiveGroups, getOffMask } from './workingHoursState';
import { getWeekdayLabels, maskBits } from './weekdays';
import { deriveScheduleDisplayMode, formatDateRu } from './workingHoursView';
import ScheduleDialog from './ScheduleDialog';
import { buildSaveOps, rowsToGroups } from './draftModel';
import { useT } from '@/contexts/I18nContext';
import HoursCheckChips from './HoursCheckChips';

// ── Read-only summary ─────────────────────────────────────────────────────────

function formatDayRange(dayBits: number[], labels: string[]): string {
  if (!dayBits.length) return '';
  const runs: number[][] = [];
  let run = [dayBits[0]];
  for (let i = 1; i < dayBits.length; i++) {
    if (dayBits[i] === dayBits[i - 1] + 1) {
      run.push(dayBits[i]);
    } else {
      runs.push(run);
      run = [dayBits[i]];
    }
  }
  runs.push(run);
  return runs
    .map(r => r.length >= 2
      ? `${labels[r[0]]}–${labels[r[r.length - 1]]}`
      : labels[r[0]])
    .join(', ');
}

function ScheduleSummary({ groups }: { groups: ScheduleGroup[] }) {
  const { lang } = useT();
  const mode = deriveScheduleDisplayMode(groups);
  const activeGroups = getActiveGroups(groups);
  const offBits = maskBits(getOffMask(groups));
  const labels = getWeekdayLabels(lang);
  const aroundClock = lang === 'en' ? 'Open 24/7' : 'Круглосуточно';
  const closed = lang === 'en' ? 'Closed' : 'Закрыто';
  const closedHint = lang === 'en' ? 'every day is a day off' : 'все дни выходные';
  const dayOff = lang === 'en' ? 'closed' : 'выходные';

  if (mode === 'summary_24x7') {
    return (
      <p className="text-base font-normal">
        {aroundClock}{' '}
        <span className="text-muted-foreground">24/7</span>
      </p>
    );
  }

  if (mode === 'summary_closed') {
    return (
      <p className="text-base font-normal">
        {closed}{' '}
        <span className="text-muted-foreground">{closedHint}</span>
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {activeGroups.map(g => (
        <p key={g.localId} className="text-base font-normal">
          {formatDayRange(maskBits(g.mask), labels)}{'  '}
          <span className="text-muted-foreground">{g.openTime} – {g.closeTime}</span>
        </p>
      ))}
      {offBits.length > 0 && (
        <p className="text-base font-normal text-muted-foreground">
          {formatDayRange(offBits, labels)} — {dayOff}
        </p>
      )}
    </div>
  );
}

// ── Data container ────────────────────────────────────────────────────────────

interface Props {
  zoneId: string;
}

const ScheduleDisplay: React.FC<Props> = ({ zoneId }) => {
  const { t } = useT();
  const { data: rows = [], isLoading, mutate } = useSWR<ZoneWorkingHoursDto[]>(
    `working-hours:${zoneId}`,
    async () => (await listZoneWorkingHours(zoneId)).data.results,
  );

  const serverGroups = useMemo(() => rowsToGroups(rows), [rows]);
  const today = new Date().toISOString().slice(0, 10);
  const dateRows = useMemo(
    () => rows
      .filter(r => r.date !== null && r.date! >= today)
      .sort((a, b) => (a.date! < b.date! ? -1 : 1)),
    [rows, today],
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  async function handleSaveAll(groups: ScheduleGroup[], exceptions: ScheduleExceptionDraft[]) {
    const ops = buildSaveOps(serverGroups, dateRows, groups, exceptions, zoneId);
    await Promise.all(ops.map(o => {
      if (o.op === 'delete') return deleteZoneWorkingHours(o.id);
      if (o.op === 'create') return createZoneWorkingHours(o.payload);
      return updateZoneWorkingHours(o.id, o.payload);
    }));
    await mutate();
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5">
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
          {t('general.workingHours')}
        </div>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="inline-block rounded-md px-2 py-1 text-left transition-colors hover:bg-muted"
        >
          <ScheduleSummary groups={serverGroups} />
          {dateRows.length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-1">
              {dateRows.map((row, i) => (
                <span key={row.id} className="text-xs text-muted-foreground">
                  {formatDateRu(row.date!)}
                  {row.open_time
                    ? ` ${row.open_time.slice(0, 5)}–${row.close_time?.slice(0, 5)}`
                    : ' вых.'}
                  {i < dateRows.length - 1 ? ',' : ''}
                </span>
              ))}
            </div>
          )}
        </button>
        <HoursCheckChips zoneId={zoneId} />
      </div>

      <ScheduleDialog
        open={dialogOpen}
        initialGroups={serverGroups}
        exceptionRows={dateRows}
        today={today}
        onClose={() => setDialogOpen(false)}
        onSaveAll={handleSaveAll}
      />
    </>
  );
};

export default ScheduleDisplay;
